import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { Observable, catchError, of, tap } from 'rxjs';

interface RoleRule {
  allowedRoles?: string[] | string;
  deniedRoles?: string[] | string;
  roles?: string[] | string;
  blockedRoles?: string[] | string;
  allowedUsers?: string[] | string;
  deniedUsers?: string[] | string;
  users?: string[] | string;
  blockedUsers?: string[] | string;
}

interface TooltipRoleRule extends RoleRule {
  fields?: Record<string, RoleRule>;
}

interface RoleVisibilityPolicy {
  labels?: RoleRule;
  tooltip?: TooltipRoleRule;
}

interface RoleVisibilityMapping {
  policies?: Record<string, RoleVisibilityPolicy>;
  widgets?: Record<string, RoleVisibilityPolicy>;
}

@Injectable({
  providedIn: 'root',
})
export class RoleVisibilityService {
  private mapping: RoleVisibilityMapping = {};
  private loaded = false;

  constructor(
    private http: HttpClient,
    private keycloakService: KeycloakService,
  ) {}

  loadMapping(url = 'assets/role-visibility-config/chart-role-visibility.json'): Observable<RoleVisibilityMapping> {
    if (this.loaded) return of(this.mapping);

    return this.http.get<RoleVisibilityMapping>(url).pipe(
      tap((mapping) => {
        this.mapping = mapping || {};
        this.loaded = true;
      }),
      catchError(() => {
        this.mapping = {};
        this.loaded = true;
        return of(this.mapping);
      }),
    );
  }

  applyToDashboardConfig(config: any): any {
    if (!config?.pageContent?.length) return config;

    return {
      ...config,
      pageContent: this.applyToNode(config.pageContent),
    };
  }

  private applyToNode(node: any): any {
    if (Array.isArray(node)) {
      return node.map((item) => this.applyToNode(item));
    }

    if (!node || typeof node !== 'object') {
      return node;
    }

    if (node.type) {
      return this.applyToWidget(node);
    }

    const next: Record<string, unknown> = {};
    Object.entries(node).forEach(([key, value]) => {
      next[key] = this.applyToNode(value);
    });
    return next;
  }

  private applyToWidget(widget: any): any {
    const policy = this.resolvePolicy(widget);
    if (!policy) return widget;

    const next = {
      ...widget,
      chartConfig: {
        ...(widget.chartConfig || {}),
      },
    };

    if (!this.hasRoleAccess(policy.labels)) {
      next.chartConfig.showLabels = false;
      next.chartConfig.showDataLabels = false;
      next.chartConfig.showBarLabels = false;
      next.chartConfig.showLineLabels = false;
      next.showLabels = false;
    }

    if (!this.hasRoleAccess(policy.tooltip)) {
      next.chartConfig.tooltip = {
        ...(next.chartConfig.tooltip || {}),
        show: false,
      };
      next.tooltip = false;
      return next;
    }

    next.chartConfig.tooltip = this.filterTooltipFields(
      next.chartConfig.tooltip,
      policy.tooltip?.fields,
    );

    return next;
  }

  private resolvePolicy(widget: any): RoleVisibilityPolicy | null {
    const policyId = String(
      widget.visibilityPolicyId ||
      widget.roleVisibilityPolicy ||
      widget.accessPolicyId ||
      '',
    ).trim();

    const policies = this.mapping.policies || {};
    const normalizedPolicies = this.normalizeMappingKeys(policies);
    if (policyId && normalizedPolicies[policyId]) {
      return normalizedPolicies[policyId];
    }

    const widgetId = String(widget.widgetId || '').trim();
    const widgets = this.normalizeMappingKeys(this.mapping.widgets || {});
    return (widgetId && widgets[widgetId]) || null;
  }

  private normalizeMappingKeys<T>(mapping: Record<string, T>): Record<string, T> {
    return Object.entries(mapping).reduce((result, [key, value]) => {
      result[String(key || '').trim()] = value;
      return result;
    }, {} as Record<string, T>);
  }

  private filterTooltipFields(tooltip: any, fieldRules?: Record<string, RoleRule>): any {
    if (!tooltip || !Array.isArray(tooltip.fields) || !fieldRules) {
      return tooltip;
    }

    return {
      ...tooltip,
      fields: tooltip.fields.filter((field: any) => {
        const key = String(field?.key || '').trim();
        return !key || this.hasRoleAccess(fieldRules[key]);
      }),
    };
  }

  private hasRoleAccess(rule?: RoleRule | null): boolean {
    if (!rule) return true;

    const userRoles = this.getCurrentUserRoles();
    const userKeys = this.getCurrentUserKeys();
    const deniedUsers = this.normalizeRoles(rule.deniedUsers || rule.blockedUsers);
    if (deniedUsers.some((user) => userKeys.includes(user.toLowerCase()))) {
      return false;
    }

    const deniedRoles = this.normalizeRoles(rule.deniedRoles || rule.blockedRoles);
    if (deniedRoles.some((role) => userRoles.includes(role))) {
      return false;
    }

    const allowedUsers = this.normalizeRoles(rule.allowedUsers || rule.users);
    if (allowedUsers.length) {
      return allowedUsers.some((user) => userKeys.includes(user.toLowerCase()));
    }

    const allowedRoles = this.normalizeRoles(rule.allowedRoles || rule.roles);
    if (allowedRoles.length) {
      return allowedRoles.some((role) => userRoles.includes(role));
    }

    return true;
  }

  private getCurrentUserKeys(): string[] {
    const tokenParsed = this.keycloakService.getKeycloakInstance()?.tokenParsed as any;
    const keys = [
      tokenParsed?.preferred_username,
      tokenParsed?.email,
      tokenParsed?.name,
      tokenParsed?.sub,
      tokenParsed?.sid,
    ];

    return keys
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean);
  }

  private getCurrentUserRoles(): string[] {
    const roles = new Set<string>();

    try {
      this.keycloakService.getUserRoles().forEach((role) => roles.add(role));
    } catch {
      /* ignore */
    }

    const tokenParsed = this.keycloakService.getKeycloakInstance()?.tokenParsed as any;
    tokenParsed?.realm_access?.roles?.forEach((role: string) => roles.add(role));

    Object.values(tokenParsed?.resource_access || {}).forEach((resource: any) => {
      resource?.roles?.forEach((role: string) => roles.add(role));
    });

    return Array.from(roles);
  }

  private normalizeRoles(value: string[] | string | undefined): string[] {
    if (Array.isArray(value)) {
      return value.map((role) => String(role || '').trim()).filter(Boolean);
    }

    if (typeof value === 'string') {
      return value.split(',').map((role) => role.trim()).filter(Boolean);
    }

    return [];
  }
}
